import { useCallback, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';

import { Field, SettingsProps } from './response.constants';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { PStyled } from 'approot/shared/styles/typography.style';
import {
  Sortable,
  SortableHeader,
  SortableItem,
  SortableItemButton,
} from 'approot/shared/sortable/sortable';
import {
  MultiView,
  MultiViewPage,
} from 'approot/shared/layout/layout-multi-view';
import ResponseSettingsField from './response-settings-field';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import customNanoId from 'lib/nanoid/nanoid';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import { DIRECTION_LEFT } from 'webclient.constants';
import { Box } from 'approot/shared/design-system/box/box.style';
import { getSuggestedAnswerItemPayload } from '../shared/logic/get-suggested-answer-item-payload';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

const DEFAULT_PLACEHOLDER = 'Add answer';

const ResponseSettings = ({
  id,
  sectionId,
  type,
  properties,
  suggestedAnswerProperties,
  onCloseChildDrawer,
}: SettingsProps) => {
  const [selectedFieldId, setSelectedFieldId] = useState<string>();
  const { closeDrawer, setEditModeTargetElement } = useActivityBuilderContext();

  const isChildDrawer = !!onCloseChildDrawer;

  const fields = properties.textResponse.fields || [];
  const selectedField = fields.find(f => f.itemId === selectedFieldId);

  const sortedFields = useMemo(
    () => sortBy(properties.textResponse.fields, 'sort'),
    [properties.textResponse.fields]
  );

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const handleOnChange = useCallback(
    ({
      updatedField,
      existingFields,
    }: {
      updatedField: Field;
      existingFields: Field[];
    }) => {
      const updatedFields: Field[] = existingFields.slice();
      const index = updatedFields.findIndex(
        f => f.itemId === updatedField.itemId
      );
      updatedFields[index] = {
        ...updatedField,
      };

      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            type,
            properties: {
              textResponse: {
                fields: updatedFields,
              },
            },
          },
        },
        properties: {
          ...properties,
          textResponse: {
            ...properties.textResponse,
            fields: updatedFields,
          },
        },
        typename: 'ActivitiesResponseComponent',
      });
    },
    [type, id, sectionId, properties, updateComponentProperties]
  );

  const handleAddNew = useCallback(
    (fields: Field[], sortedFields: Field[]) => {
      const newField: Field = {
        itemId: customNanoId(),
        baseColour: null,
        numberOfLines: 1,
        placeholder: DEFAULT_PLACEHOLDER,
        isPlaceholderScaffold: false,
        sort: getReorderSort(sortedFields, sortedFields.length, DIRECTION_LEFT),
        __typename: 'ResponseComponentField',
      };

      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            type,
            properties: {
              textResponse: {
                fields: [...fields, newField],
              },
            },
          },
        },
        properties: {
          ...properties,
          textResponse: {
            ...properties.textResponse,
            fields: [...fields, newField],
          },
        },
        typename: 'ActivitiesResponseComponent',
      });
    },
    [id, sectionId, type, properties, updateComponentProperties]
  );

  return (
    <MultiView>
      {multiviewProps => (
        <>
          <MultiViewPage
            {...multiviewProps}
            onClose={() => {
              setEditModeTargetElement(undefined);
              closeDrawer();
            }}
            index={0}
            renderItem={() => (
              <ActivityBuilderDrawerWrapper
                captureKeypresses={!isChildDrawer}
                onCloseByEscape={
                  isChildDrawer ? onCloseChildDrawer : closeDrawer
                }
              >
                <ActivityBuilderDrawerHeader
                  title="Text Response"
                  isBack={isChildDrawer}
                  onClose={isChildDrawer ? onCloseChildDrawer : closeDrawer}
                />
                <ActivityBuilderDrawerContent>
                  <PStyled>Add one or multiple text responses.</PStyled>
                  <Sortable<Field>
                    key={sortedFields.map(s => s.placeholder).join(',')}
                    list={sortedFields}
                    renderItem={item => {
                      return (
                        <SortableItem key={item.itemId}>
                          <SortableHeader />
                          <SortableItemButton
                            key={item.itemId}
                            onClick={() => {
                              setEditModeTargetElement(item.itemId);
                              setSelectedFieldId(item.itemId);
                              multiviewProps.goForward();
                            }}
                          >
                            {item.placeholder || DEFAULT_PLACEHOLDER}
                          </SortableItemButton>
                        </SortableItem>
                      );
                    }}
                    onSortChange={({
                      sourceIndex,
                      destinationIndex,
                      direction,
                    }) => {
                      const field = sortedFields[sourceIndex];
                      if (sourceIndex !== destinationIndex) {
                        const newSort = getReorderSort<Field>(
                          sortedFields,
                          destinationIndex,
                          direction
                        );
                        handleOnChange({
                          updatedField: {
                            ...field,
                            sort: newSort,
                          },
                          existingFields: fields,
                        });
                      }
                    }}
                  />
                  <Box $flexDirection="column" $marginBottom={24}>
                    <SecondaryButton
                      type="button"
                      onClick={() => handleAddNew(fields, sortedFields)}
                      data-selector="btn-add-response"
                    >
                      Add Response
                    </SecondaryButton>
                  </Box>
                </ActivityBuilderDrawerContent>
              </ActivityBuilderDrawerWrapper>
            )}
          />
          <MultiViewPage
            {...multiviewProps}
            onClose={() => {
              setEditModeTargetElement(undefined);
              multiviewProps.goBack();
            }}
            index={1}
            renderItem={() =>
              selectedField ? (
                <ResponseSettingsField
                  index={sortedFields.findIndex(
                    f => f.itemId === selectedFieldId
                  )}
                  field={selectedField}
                  canDelete={sortedFields.length > 1}
                  goBack={() => {
                    setEditModeTargetElement(undefined);
                    multiviewProps.goBack();
                  }}
                  handleOnChange={updatedField => {
                    handleOnChange({
                      updatedField,
                      existingFields: fields,
                    });
                  }}
                  handleDelete={removeFieldId => {
                    // remove the suggested answer
                    const suggestedAnswers =
                      suggestedAnswerProperties?.suggestedAnswers?.filter(
                        sa => sa.answerReferences?.id !== removeFieldId
                      );
                    const updatedFields = fields.filter(
                      f => f.itemId !== removeFieldId
                    );
                    updateComponentProperties({
                      input: {
                        sectionId,
                        componentId: id,
                        componentData: {
                          type,
                          properties: {
                            textResponse: {
                              fields: updatedFields,
                            },
                          },
                        },
                        suggestedAnswers: suggestedAnswers?.map(answer =>
                          getSuggestedAnswerItemPayload(answer)
                        ),
                      },
                      properties: {
                        ...properties,
                        textResponse: {
                          ...properties.textResponse,
                          fields: updatedFields,
                        },
                      },
                      typename: 'ActivitiesResponseComponent',
                    });
                  }}
                />
              ) : null
            }
          />
        </>
      )}
    </MultiView>
  );
};

export default ResponseSettings;
